import { useParams } from 'react-router-dom';
import { useUserProfile, useToggleSubscription, useMe, useUserAuctions } from '@/hooks/useUser';
import { UserHeader } from '@/components/user/UserHeader';
import { ReviewsList } from '@/components/user/ReviewsList';
import { AuctionRow } from '@/components/auction/AuctionRow';
import { Stat } from '@/components/ui/Stat';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { haptic } from '@/lib/telegram';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import styles from './UserProfilePage.module.css';

export const UserProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const goBack = useBackNavigation();
  const { data: user, isLoading, isError } = useUserProfile(id);
  const toggleSub = useToggleSubscription();
  const { data: me } = useMe();
  const { data: activeAuctions, isLoading: auctionsLoading } = useUserAuctions(id);

  const isMyProfile = me?.id === user?.id;

  if (isLoading) {
    return (
      <div className={styles.page}>
        <BackButton onBack={goBack} />
        <div className={styles.headerSkeleton}>
          <Skeleton width={96} height={96} rounded="full" />
          <Skeleton width={140} height={22} />
          <Skeleton width={80} height={14} />
        </div>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className={styles.page}>
        <BackButton onBack={goBack} />
        <EmptyState
          title="Пользователь не найден"
          description="Возможно, профиль был удалён"
        />
      </div>
    );
  }

  const handleToggle = () => {
    haptic.medium();
    toggleSub.mutate(user.id);
  };

  return (
    <div className={styles.page}>
      <BackButton onBack={goBack} />

      <div className={styles.headerWrap}>
        <UserHeader
          className={styles.profileHeader}
          name={user.display_name}
          username={user.telegram_username}
          avatarUrl={user.avatar_url}
          rating={user.rating}
          ratingCount={user.rating_count}
          memberSince={user.member_since}
          actions={
            isMyProfile ? undefined : (
              <Button
                variant={user.is_following ? 'secondary' : 'primary'}
                fullWidth
                onClick={handleToggle}
                loading={toggleSub.isPending}
              >
                {user.is_following ? 'В подписках' : 'Подписаться'}
              </Button>
            )
          }
        />
      </div>

      <Section title="Как продавец">
        <div className={styles.statsGrid}>
          <Stat label="Создано" value={user.auctions_created} />
          <Stat label="Продано" value={user.auctions_sold} variant="accent" />
          <Stat label="Отзывов" value={user.rating_count} />
        </div>
      </Section>

      <Section title="Как покупатель">
        <div className={styles.statsGrid}>
          <Stat label="Участий" value={user.bids_placed} />
          <Stat label="Побед" value={user.auctions_won} variant="accent" />
          <Stat label="Win-rate" value={`${user.win_rate}%`} />
        </div>
      </Section>

      <Section title="Активные лоты">
        {auctionsLoading ? (
          <div className={styles.auctionsList}>
            <Skeleton width="100%" height={88} />
            <Skeleton width="100%" height={88} />
          </div>
        ) : activeAuctions && activeAuctions.length > 0 ? (
          <div className={styles.auctionsList}>
            {activeAuctions.map((a) => (
              <AuctionRow key={a.id} auction={a} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="Нет активных лотов"
            description="Сейчас у этого продавца нет открытых аукционов"
          />
        )}
      </Section>

      <Section title={`Отзывы · ${user.rating_count}`}>
        <ReviewsList userId={user.id} />
      </Section>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className={styles.section}>
    <p className={styles.sectionTitle}>{title}</p>
    {children}
  </section>
);

const BackButton = ({ onBack }: { onBack: () => void }) => (
  <button
    className={styles.backBtn}
    onClick={() => { haptic.light(); onBack(); }}
    aria-label="Назад"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  </button>
);